import classnames from 'classnames';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';

import MovieTrailerPlayerButton from './MovieTrailerPlayerButton';

// Close enough to react-player's own YouTube matcher for the trailer urls we feed it.
const YOUTUBE_ID_PATTERN = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.*&v=))([\w-]{11})/;

function getYoutubeVideoId(url: string): string | undefined {
    return url.match(YOUTUBE_ID_PATTERN)?.[1];
}

type YoutubeInternalPlayer = {
    loadVideoById?: (videoId: string) => void;
    getVideoData?: () => { video_id?: string } | undefined;
};

/**
 * Pure presentational component for displaying a movie trailer video player.
 * Shows video player with thumbnail overlay and play button when not playing.
 * Supports play/pause/end events.
 *
 * Changing `videoUrl` on a mounted player switches YouTube videos via `loadVideoById` on the
 * SAME player instead of letting react-player handle the prop change. react-player only CUES a
 * new url (`cueVideoById`) and then issues a programmatic play(), which the browser's autoplay
 * policy may deny — leaving the video paused on YouTube's red play button. `loadVideoById`
 * continues the current playing session, which browsers allow.
 */
const MovieTrailerPlayer: React.FC<{
    className?: string;
    videoUrl: string;
    thumbnailUrl?: string;
    thumbnailBlurDataUrl?: string;
    // Optional: `MovieDto.title` is not guaranteed for every locale.
    movieTitle?: string;
    isPlaying: boolean;
    onPlay: () => void;
    onPause: () => void;
    onEnded?: () => void;
    onProgress?: (state: OnProgressProps) => void;
    onReady?: () => void;
    onError?: (error: unknown) => void;
    showControls?: boolean;
}> = ({
    className,
    videoUrl,
    thumbnailUrl,
    thumbnailBlurDataUrl,
    movieTitle,
    isPlaying,
    onPlay,
    onPause,
    onEnded,
    onProgress,
    onReady,
    onError,
    showControls = true
}) => {
    const playerRef = useRef<ReactPlayer | null>(null);

    // The url handed to ReactPlayer stays pinned to the FIRST YouTube url; later YouTube urls are
    // loaded imperatively (see the component doc block). Non-YouTube urls fall back to the normal
    // prop-driven path.
    const [initialVideoUrl] = useState(videoUrl);
    const playerUrl = getYoutubeVideoId(videoUrl) ? initialVideoUrl : videoUrl;

    const loadDesiredVideo = useCallback(() => {
        const desiredVideoId = getYoutubeVideoId(videoUrl);
        if (!desiredVideoId) return;
        const internalPlayer = playerRef.current?.getInternalPlayer() as YoutubeInternalPlayer | null | undefined;
        if (typeof internalPlayer?.loadVideoById !== 'function') return;
        if (internalPlayer.getVideoData?.()?.video_id === desiredVideoId) return;
        internalPlayer.loadVideoById(desiredVideoId);
    }, [videoUrl]);

    useEffect(() => {
        loadDesiredVideo();
    }, [loadDesiredVideo]);

    return (
        <div className={classnames(className, 'relative w-full h-auto rounded-8 overflow-clip')}>
            <div className="w-full h-auto aspect-[16/9]">
                {isPlaying ? (
                    <ReactPlayer
                        ref={playerRef}
                        url={playerUrl}
                        playing={isPlaying}
                        onPlay={onPlay}
                        onPause={onPause}
                        onEnded={onEnded}
                        onProgress={onProgress}
                        onReady={() => {
                            // If the desired video changed while the player was still
                            // initialising, reconcile now that the API is available.
                            loadDesiredVideo();
                            onReady?.();
                        }}
                        onError={onError}
                        controls={showControls}
                        width="100%"
                        height="100%"
                    />
                ) : (
                    <div className="relative w-full h-full">
                        {thumbnailUrl && (
                            <Image
                                className="w-full h-full object-cover"
                                alt={movieTitle || 'movie-backdrop-image'}
                                src={thumbnailUrl}
                                blurDataURL={thumbnailBlurDataUrl}
                                width={1000}
                                height={600}
                            />
                        )}

                        <MovieTrailerPlayerButton
                            onPlay={onPlay}
                            aria-label={movieTitle ? `Play ${movieTitle}` : undefined}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MovieTrailerPlayer;
